import { useEffect } from 'react';
import styled, { keyframes } from 'styled-components';
import { useT } from 'i18n';

// Animaciones
const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(20px); }
  to { opacity: 1; transform: translateY(0); }
`;

// Styled Components
const Container = styled.div`
  animation: ${fadeIn} 0.4s ease-out;
`;

const GridContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 1.5rem;
  
  @media (max-width: 992px) {
    grid-template-columns: 1fr;
  }
`;

const InfoCard = styled.div`
  background: ${props => props.theme === 'white'
    ? '#fff'
    : 'rgba(12,14,19, 0.6)'};
  backdrop-filter: blur(10px);
  border-radius: 20px;
  border: 1px solid ${props => props.theme === 'white'
    ? 'rgba(0, 0, 0, 0.1)'
    : 'rgba(227,168,43, 0.2)'};
  overflow: hidden;
  transition: transform 0.3s ease, box-shadow 0.3s ease;
  
  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 10px 30px rgba(227,168,43, 0.2);
  }
`;

const CardHeader = styled.div`
  padding: 1.5rem;
  background: linear-gradient(135deg, rgba(227,168,43, 0.15), rgba(23,27,36, 0.1));
  border-bottom: 1px solid ${props => props.theme === 'white'
    ? 'rgba(0, 0, 0, 0.05)'
    : 'rgba(227,168,43, 0.2)'};
  
  h1 {
    font-size: 1.3rem;
    font-weight: 700;
    margin-bottom: 0.5rem;
    background: rgba(227,168,43,.14);
    -webkit-background-clip: text;
    background-clip: text;
    color: transparent;
  }
  
  p {
    color: ${props => props.theme === 'white' ? '#666' : 'rgba(255, 255, 255, 0.7)'};
    font-size: 0.85rem;
    margin-bottom: 0.25rem;
    
    small {
      color: #e3a82b;
    }
  }
`;

const CardBody = styled.div`
  padding: 1.5rem;
`;

const InfoRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  padding: 0.75rem 0;
  border-bottom: 1px solid ${props => props.theme === 'white'
    ? 'rgba(0, 0, 0, 0.05)'
    : 'rgba(255, 255, 255, 0.05)'};
  
  &:last-child {
    border-bottom: none;
  }
  
  strong {
    color: ${props => props.theme === 'white' ? '#666' : 'rgba(255, 255, 255, 0.7)'};
    font-size: 0.85rem;
    font-weight: 500;
  }
  
  span {
    color: ${props => props.theme === 'white' ? '#333' : '#fff'};
    font-weight: 600;
    text-align: right;
    
    &.empty {
      color: #ff5252;
      font-weight: normal;
    }
  }
`;

const ButtonContainer = styled.div`
  margin-top: 2rem;
  text-align: center;
`;

const SubmitButton = styled.button`
  width: 100%;
  padding: 1rem;
  border-radius: 50px;
  border: none;
  background: rgba(227,168,43,.14);
  color: #e3a82b;
  font-weight: 700;
  font-size: 1rem;
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 25px rgba(227,168,43, 0.3);
  }
`;

const EmptyValue = styled.span`
  color: #ff5252;
  font-style: italic;
`;

const DatosPersonales = ({ usuario, jimf_theme, setCompletar }) => {
  const { t } = useT();
  const isWhiteTheme = jimf_theme === 'white';

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Helper para mostrar valor o "Sin agregar"
  const renderValue = (value) => {
    if (!value || value === '') {
      return <EmptyValue>{t('cuenta.sinAgregar')}</EmptyValue>;
    }
    return <span>{value}</span>;
  };

  return (
    <Container>
      <GridContainer>
        {/* Columna izquierda */}
        <InfoCard theme={isWhiteTheme ? 'white' : 'dark'}>
          <CardHeader theme={isWhiteTheme ? 'white' : 'dark'}>
            <h1>{t('cuenta.actualizacionTitulo')}</h1>
            <p>
              {t('cuenta.bienvenida1')}{' '}
              <small>{t('cuenta.bienvenida2')}</small> {t('cuenta.bienvenida3')} <small>Panther7</small>
            </p>
            <p>
              <small>{t('cuenta.obligatorios')}</small>
            </p>
          </CardHeader>
          <CardBody>
            <InfoRow theme={isWhiteTheme ? 'white' : 'dark'}>
              <strong>{t('cuenta.lblNombreUsuario')}</strong>
              {renderValue(usuario?.name)}
            </InfoRow>
            <InfoRow theme={isWhiteTheme ? 'white' : 'dark'}>
              <strong>{t('cuenta.lblCorreo')}</strong>
              {renderValue(usuario?.email)}
            </InfoRow>
            <InfoRow theme={isWhiteTheme ? 'white' : 'dark'}>
              <strong>{t('cuenta.lblReferido')}</strong>
              {renderValue(usuario?.referrer)}
            </InfoRow>
            <InfoRow theme={isWhiteTheme ? 'white' : 'dark'}>
              <strong>{t('cuenta.lblDocumento')}</strong>
              {renderValue(usuario?.identity)}
            </InfoRow>
            <InfoRow theme={isWhiteTheme ? 'white' : 'dark'}>
              <strong>{t('cuenta.lblSexo')}</strong>
              {renderValue(usuario?.sexo)}
            </InfoRow>
            <InfoRow theme={isWhiteTheme ? 'white' : 'dark'}>
              <strong>{t('cuenta.lblSituacionLaboral')}</strong>
              {renderValue(usuario?.situacionlaboral)}
            </InfoRow>
            <InfoRow theme={isWhiteTheme ? 'white' : 'dark'}>
              <strong>{t('cuenta.lblEmpresa')}</strong>
              {renderValue(usuario?.comercial_name)}
            </InfoRow>
            <InfoRow theme={isWhiteTheme ? 'white' : 'dark'}>
              <strong>{t('cuenta.lblNacimiento')}</strong>
              {renderValue(usuario?.birthday_date)}
            </InfoRow>
          </CardBody>
        </InfoCard>

        {/* Columna derecha */}
        <InfoCard theme={isWhiteTheme ? 'white' : 'dark'}>
          <CardHeader theme={isWhiteTheme ? 'white' : 'dark'}>
            <h1>{t('cuenta.contactoTitulo')}</h1>
            <p>{t('cuenta.contactoAyuda')}</p>
          </CardHeader>
          <CardBody>
            <InfoRow theme={isWhiteTheme ? 'white' : 'dark'}>
              <strong>{t('cuenta.lblCelular')}</strong>
              {renderValue(usuario?.telephone)}
            </InfoRow>
            <InfoRow theme={isWhiteTheme ? 'white' : 'dark'}>
              <strong>{t('cuenta.lblDireccion')}</strong>
              {renderValue(usuario?.address_street)}
            </InfoRow>
            <InfoRow theme={isWhiteTheme ? 'white' : 'dark'}>
              <strong>{t('cuenta.lblCalle')}</strong>
              {renderValue(usuario?.address_number)}
            </InfoRow>
            <InfoRow theme={isWhiteTheme ? 'white' : 'dark'}>
              <strong>{t('cuenta.lblPostal')}</strong>
              {renderValue(usuario?.address_zipcode)}
            </InfoRow>
            <InfoRow theme={isWhiteTheme ? 'white' : 'dark'}>
              <strong>{t('cuenta.lblPais')}</strong>
              {renderValue(usuario?.address_country)}
            </InfoRow>
            <InfoRow theme={isWhiteTheme ? 'white' : 'dark'}>
              <strong>{t('cuenta.lblProvincia')}</strong>
              {renderValue(usuario?.address_zone)}
            </InfoRow>
            <InfoRow theme={isWhiteTheme ? 'white' : 'dark'}>
              <strong>{t('cuenta.lblCiudad')}</strong>
              {renderValue(usuario?.address_city)}
            </InfoRow>
          </CardBody>
        </InfoCard>
      </GridContainer>

      {/* Botón de acción */}
      <ButtonContainer>
        <SubmitButton onClick={() => setCompletar(true)}>
          {t('cuenta.completarInfo')}
        </SubmitButton>
      </ButtonContainer>

      <div className="window-select-tipe mt-4">
        <h1><i className="tim-icons icon-single-02 mr-2" /> {t('cuenta.completeInfoPersonal')}</h1>
      </div>
    </Container>
  );
};

export default DatosPersonales;